import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  IconButton,
  Box,
  Menu,
  MenuItem,
} from "@mui/material";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useLocation } from "react-router-dom";
import { getTitleFromPath, getParentRoute } from "../../utils/routesToTitles";
import Role from "../../utils/roles";
import FrameworkSelector from "./FrameworkSelector";
import { useRestrictedModuleAccess } from "../AccessControl";

const Navbar = ({ isEmployeeMode, handleSwitchRole, dashboardData }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth0();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const isRestrictedAccess = useRestrictedModuleAccess();

  const title = getTitleFromPath(location.pathname);
  const parentRoute = getParentRoute(location.pathname);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogOut = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
    setAnchorEl(null);
  };

  const toggleRoleAndNavigate = () => {
    const newRole = !isEmployeeMode;
    handleSwitchRole(newRole); // Update role in parent
    const route = newRole ? "/employee/dashboard" : "/admin/dashboard";
    navigate(route); // Navigate only when role toggles
    setAnchorEl(null); // Close menu
  };

  const userDetails = dashboardData?.getAccountDetails?.userDetails || {};
  const { name } = userDetails;
  const isAdmin = userDetails?.roles?.some(
    (role) => role?.role_description == Role.ADMIN
  );

  return (
    <AppBar
      elevation={0}
      sx={{
        position: "sticky",
        top: 0,
        background: "#FFFFFF",
        boxShadow:
          "0px 21px 8px rgba(0, 0, 0, 0.01), 0px 12px 7px rgba(0, 0, 0, 0.03), 0px 5px 5px rgba(0, 0, 0, 0.04), 0px 1px 3px rgba(0, 0, 0, 0.05)",
        height: "65px",
        zIndex: "100 !important",
      }}
      color="transparent"
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "60px",
          padding: "0 24px !important",
          minHeight: "60px !important",
        }}
      >
        {/* Left Section */}
        <Box display="flex" alignItems="center" gap="12px">
          {/* Dashboard Title */}
          <Typography
            variant="h5"
            noWrap
            sx={{
              fontWeight: 600,
              fontSize: "18px",
              color: "#1F2937",
              lineHeight: "28px",
            }}
          >
            {title}
          </Typography>

          {/* Framework Selector */}
          <FrameworkSelector />
        </Box>

        {/* Right Section */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* User Avatar */}
          {isAuthenticated && user ? (
            <>
              <Avatar
                alt="User Avatar"
                sx={{
                  width: 32,
                  height: 32,
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: 600,
                  backgroundColor: "#6366F1",
                  color: "#FFFFFF",
                }}
                onClick={handleClick}
              >
                {name
                  ?.split(" ")
                  .map((word, index) => {
                    if (index === 0 || index === name.split(" ").length - 1) {
                      return word.charAt(0);
                    }
                    return null;
                  })
                  .join("")}
              </Avatar>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                sx={{
                  "& .MuiPaper-root": {
                    mt: 0.5,
                    minWidth: "160px",
                    boxShadow:
                      "0px 4px 6px rgba(0, 0, 0, 0.1), 0px -2px 4px rgba(0, 0, 0, 0.05), 4px 0px 6px rgba(0, 0, 0, 0.05), -4px 0px 6px rgba(0, 0, 0, 0.05);",
                    borderRadius: "6px",
                  },
                }}
              >
                {isAdmin && !isRestrictedAccess && (
                  <MenuItem
                    onClick={toggleRoleAndNavigate}
                    sx={{
                      fontSize: "14px",
                      py: 1.5,
                      px: 2,
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}
                  >
                    {isEmployeeMode ? "Switch to Admin" : "Switch to Employee"}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={handleLogOut}
                  sx={{
                    fontSize: "14px",
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "#F9FAFB",
                    },
                  }}
                >
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Avatar
              alt="Andy Warhol"
              src="https://i.pravatar.cc/300"
              sx={{ width: 32, height: 32 }}
            />
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
